import _ from "lodash";
import React from "react";
import ReactDOMServer from "react-dom/server";
import moment from "moment";

import {
  getDefaultOrderBy as getDefaultOrderByStudent,
  getFormattedStudentName,
} from "Utils/GetFormattedStudentName";
import {
  highlightText,
  highlightTextWithCustomStyle,
} from "Utils/highlightText";
import { STAFF, STUDENT, PARENT } from "Constants/stringConstants";
import { colors } from "Constants";
import ACLStore from "lib/aclStore";
import { getStyleStrippedText } from "./htmlSanitize";
import { getFormattedUserName } from "./GetFormattedUserName";

export const recipientModalPaddingStyles = {
  paddingLeft: "32px",
  paddingRight: "32px",
  width: "100%",
};

export const ATTACHMENT_TYPES = {
  IMAGE: "IMAGE",
  VIDEO: "VIDEO",
  FILE: "FILE",
  AUDIO: "AUDIO",
  LINK: "LINK",
};

export const CHAT_TYPE = {
  CHAT: "chat",
  CHANNEL: "channel",
};

// For the successful sent messages, we will have the status as undefined
export const MESSAGE_STATUS = {
  SENDING: "SENDING",
  FAILED: "FAILED",
};

export const MESSAGE_TYPE = {
  NORMAL: "NORMAL",
  CHANNEL_ACTIVITY: "CHANNEL_ACTIVITY",
  CONVERSATION_SUMMARY: "CONVERSATION_SUMMARY",
  ASSIGNMENT_ACTIVITY: "ASSIGNMENT_ACTIVITY",
  STUDENT_ASSIGNMENT_ACTIVITY: "STUDENT_ASSIGNMENT_ACTIVITY",
  ASSESSMENT_ACTIVITY: "ASSESSMENT_ACTIVITY",
};

export const CHAT_SCREEN_MODE = {
  WIDGET: "widget",
  FULL_SCREEN: "full",
  VIEW_MODE_SCREEN: "view_mode",
  SIDE_PANE: "side_pane",
};

export const CHANNEL_ACTIVITY_TYPES = {
  USER_ADD: "USER_ADD",
  USER_REMOVE: "USER_REMOVE",
  LABEL_CHANGE: "LABEL_CHANGE",
  DESCRIPTION_CHANGE: "DESCRIPTION_CHANGE",
};

export const NORMAL_MESSAGE_ASSIGNMENT_ACTIVITY_TYPES = [
  "STUDENT_ASSIGNMENT_SUBMISSION",
  "UNHAND_STUDENT_ASSIGNMENT_SUBMISSION",
  "STUDENT_ASSIGNMENT_EVALUATED",
  "STUDENT_ASSIGNMENT_SHARED",
  "STUDENT_ASSIGNMENT_EVALUATED_SHARED",
];

export const NORMAL_MESSAGE_ASSIGNMENT_ALERT_TYPES = [
  "ASSIGNMENT_SUBMISSION_OVERDUE_ALERT",
  "ASSIGNMENT_SUBMISSION_PENDING_ALERT",
  "ASSIGNMENT_DUE_DATE_APPROACHING_ALERT",
  "ASSIGNMENT_CUSTOM_ALERT",
];

export const getIsMessagingWidget = ({ screenMode }) =>
  screenMode === CHAT_SCREEN_MODE.WIDGET;

export const getIsViewMode = ({ screenMode }) =>
  screenMode === CHAT_SCREEN_MODE.VIEW_MODE_SCREEN;

export const getIsSidePaneMode = ({ screenMode }) =>
  screenMode === CHAT_SCREEN_MODE.SIDE_PANE;

export const getMessageDOMId = message =>
  `Conversation-${message.type}-${message.id}`;

export const getParentContainer = parentId =>
  document.getElementById(`chat-scroll-container-${parentId}`);

export const getFormattedNameForUserType = ({ user, orderBy }) => {
  if (user.type === STUDENT) {
    return getFormattedUserName({
      user,
      orderBy,
    });
  }
  return getFormattedUserName({ user });
};

export const getMentionsHtml = (item, searchText) => {
  const highlightedText = searchText
    ? highlightTextWithCustomStyle(item, searchText)
    : item;

  return ReactDOMServer.renderToStaticMarkup(
    <span
      style={{ color: colors.textLink }}
      dangerouslySetInnerHTML={{
        __html: `@${getStyleStrippedText({
          text: highlightedText,
          type: "SANITIZE_DANGER_HTML_PARAGRAPH",
        })}`,
      }}
    />
  );
};

export const renderLinkInMessage = ({ match, searchText }) => {
  const href = match.getAnchorHref();
  const anchorText = match.getAnchorText();
  const highlightedText = searchText
    ? highlightText(anchorText, searchText)
    : anchorText;

  return ReactDOMServer.renderToStaticMarkup(
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={anchorText}
      dangerouslySetInnerHTML={{
        __html: getStyleStrippedText({
          text: highlightedText,
          type: "SANITIZE_DANGER_HTML_PARAGRAPH",
        }),
      }}
    />
  );
};

// undefined granularity means comparing by milliseconds
export const areMomentsSame = ({ date1, date2, granularity = undefined }) =>
  moment(date1).isSame(date2, granularity);

export const scrollOnlyIfNearBottom = ({
  threshold = 60,
  parentId,
  behavior = "smooth",
  afterCalculationCb = () => {},
} = {}) => {
  const containerElement = getParentContainer(parentId);
  if (!containerElement) return;

  const isNearBottom =
    Math.abs(
      containerElement.scrollHeight -
        containerElement.scrollTop -
        containerElement.clientHeight
    ) < threshold;

  afterCalculationCb(isNearBottom);
  if (!isNearBottom) return;
  setTimeout(() => {
    containerElement.scrollTo({
      top: containerElement.scrollHeight,
      behavior,
    });
  });
};

export const getSubText = ({
  messagedUserRole,
  messagedUserObj,
  t,
  defaultStudentNameSortPreference = getDefaultOrderByStudent(),
}) => {
  switch (messagedUserRole) {
    case STUDENT: {
      const grade = _.get(messagedUserObj, "yearGroup.grade.name");
      return grade
        ? t("common:student_hyphen_label", {
            label: grade,
          })
        : t("common:student");
    }

    case STAFF:
      return messagedUserObj.displayRole;

    case PARENT: {
      const childrenCount = _.size(messagedUserObj.children);
      if (childrenCount === 1) {
        return t("common:family_member_with_label", {
          label: getFormattedStudentName({
            student: _.first(messagedUserObj.children),
            orderBy: defaultStudentNameSortPreference,
          }),
        });
      } else if (childrenCount > 1) {
        return t("common:family_member_with_label_and_count", {
          label: getFormattedStudentName({
            student: _.first(messagedUserObj.children),
            orderBy: defaultStudentNameSortPreference,
          }),
          count: childrenCount,
        });
      }

      return t("common:family_of_count_student_other", {
        count: 0,
      });
    }
    default:
      break;
  }
};

export const getIsAIDebugModeEnabled = () =>
  ACLStore.can("IdentityAccess:EnableAIDebugMode") ||
  ACLStore.can("Common:EnableAIDebugMode");

export const scrollToBottom = ({ parentId, scrollInNextTick = false }) => {
  const scrollElement = getParentContainer(parentId);
  if (!scrollElement) return;

  if (scrollInNextTick) {
    setTimeout(() => {
      scrollElement?.scrollTo?.({ top: scrollElement.scrollHeight });
    });
  } else {
    scrollElement.scrollTo({ top: scrollElement.scrollHeight });
  }
};
